import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { format } from "date-fns";
import { Space, Breadcrumb, Card, Typography, Empty, Spin, Input, Row, Col } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { WifiOutlined, ClockCircleOutlined, SearchOutlined, EnvironmentOutlined } from "@ant-design/icons";

import type { GetPageCallbackFunc } from "../../components/ResponsiveDataTable";
import ResponsiveDataTable from "../../components/ResponsiveDataTable";
import MqttDeviceStore, { MqttDeviceData } from "../../stores/MqttDeviceStore";
import { useTitle } from "../helpers";

interface MqttDeviceListItem {
  serial: string;
  lastSeenAt?: number;
  latestData?: MqttDeviceData | null;
}

function MqttDevices() {
  const [devices, setDevices] = useState<MqttDeviceListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  const [displayedDevices, setDisplayedDevices] = useState<MqttDeviceListItem[]>([]);
  
  useTitle("DevStat Details", "Device Management");

  // Filter devices based on search term
  const filteredDevices = devices.filter(device =>
    device.serial.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    loadDevices();
  }, []);

  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update displayed devices when filtered devices or search term changes
  useEffect(() => {
    // Show all devices for both mobile and desktop
    setDisplayedDevices(filteredDevices);
  }, [filteredDevices, searchTerm]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  // Helper function to recursively search for latitude and longitude in nested objects
  const findLatLngRecursively = (obj: any, path: string = ''): { lat: any, lng: any, path: string } | null => {
    if (obj === null || obj === undefined) return null;
    
    // Check if current object has latitude and longitude
    if (obj.latitude !== undefined && obj.longitude !== undefined) {
      return { lat: obj.latitude, lng: obj.longitude, path };
    }
    
    // If it's an object, recursively search its properties
    if (typeof obj === 'object' && !Array.isArray(obj)) {
      for (const [key, value] of Object.entries(obj)) {
        const currentPath = path ? `${path}.${key}` : key;
        const result = findLatLngRecursively(value, currentPath);
        if (result) return result;
      }
    }
    
    // If it's an array, search each element
    if (Array.isArray(obj)) {
      for (let i = 0; i < obj.length; i++) {
        const currentPath = `${path}[${i}]`;
        const result = findLatLngRecursively(obj[i], currentPath);
        if (result) return result;
      }
    }
    
    return null;
  };

  const getDeviceLocation = (latestData: MqttDeviceData | null | undefined) => {
    if (!latestData) return "Unknown";
    
    try {
      const data = JSON.parse(latestData.jsonData);
      
      // Recursively search for latitude and longitude
      const location = findLatLngRecursively(data);
      
      if (location) {
        return `${location.lat}, ${location.lng}`;
      }
      
      return "Unknown";
    } catch (error) {
      console.error('Error parsing JSON data for location:', error);
      return "Unknown";
    }
  };



  const loadDevices = async () => {
    setLoading(true);
    
    try {
      // Get unique serials
      await MqttDeviceStore.getUniqueSerials(async (serials) => {
        // Filter out empty or invalid serial numbers
        const validSerials = serials.filter(serial => 
          serial && serial.trim() !== '' && serial !== 'null' && serial !== 'undefined'
        );
        
        const devicePromises = validSerials.map(serial => 
          new Promise<MqttDeviceListItem>((resolve) => {
            MqttDeviceStore.getLatestDataBySerial(serial, (latestData) => {
              resolve({
                serial,
                lastSeenAt: latestData?.lastReadingAt,
                latestData
              });
            });
          })
        );

        const deviceList = await Promise.all(devicePromises);
        // Show all devices - let the status logic handle inactive vs active display
        setDevices(deviceList);
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading devices:', error);
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Serial Number",
      dataIndex: "serial",
      key: "serial",
      width: 200,
      sorter: true,
      render: (text: string, record: MqttDeviceListItem) => (
        <Link to={`/mqtt-devices/${record.serial}`}>
          <Typography.Text strong>{text}</Typography.Text>
        </Link>
      ),
    },
    {
      title: "Last Seen",
      dataIndex: "lastSeenAt",
      key: "lastSeenAt",
      width: 200,
      sorter: true,
      render: (lastSeenAt: number | undefined) => {
        if (!lastSeenAt) return "Never";
        const date = new Date(lastSeenAt * 1000);
        return format(date, "yyyy-MM-dd HH:mm:ss");
      },
    },
    {
      title: "Status",
      dataIndex: "latestData",
      key: "status",
      width: 150,
      sorter: true,
      render: (latestData: MqttDeviceData | undefined) => {
        if (!latestData) return <Typography.Text type="secondary">Offline</Typography.Text>;
        
        // Check if device is inactive first
        if (!latestData.active) {
          return <Typography.Text type="secondary">Inactive</Typography.Text>;
        }
        
        const now = Math.floor(Date.now() / 1000);
        const timeDiff = now - latestData.lastReadingAt;
        
        if (timeDiff < 86400) { // 24 hours
          // Check if most recent data is valid
          if (latestData.validData) {
            return <Typography.Text type="success">Online - Success</Typography.Text>;
          } else {
            return <Typography.Text type="warning">Online - Invalid</Typography.Text>;
          }
        } else {
          return <Typography.Text type="danger">Offline</Typography.Text>;
        }
      },
    },
    {
      title: "Location",
      dataIndex: "latestData",
      key: "location",
      width: 150,
      sorter: true,
      render: (latestData: MqttDeviceData | undefined) => {
        const location = getDeviceLocation(latestData);
        return (
          <span>
            <EnvironmentOutlined style={{ marginRight: 8 }} />
            <Typography.Text type={location === "Unknown" ? "secondary" : undefined}>
              {location}
            </Typography.Text>
          </span>
        );
      },
    },
    {
      title: "Latest Data",
      dataIndex: "latestData",
      key: "latestData",
      sorter: true,
      render: (latestData: MqttDeviceData | undefined) => {
        if (!latestData) return <Typography.Text type="secondary">No data</Typography.Text>;
        
        try {
          const data = JSON.parse(latestData.jsonData);
          const keys = Object.keys(data).slice(0, 3); // Show first 3 keys
          return (
            <Typography.Text type="secondary">
              {keys.map(key => `${key}: ${data[key]}`).join(", ")}
              {Object.keys(data).length > 3 && "..."}
            </Typography.Text>
          );
        } catch (error) {
          console.error('Error parsing JSON data:', error, 'Raw data:', latestData.jsonData);
          return <Typography.Text type="secondary">Invalid data</Typography.Text>;
        }
      },
    },
  ];

  const getPage = (
    limit: number,
    offset: number,
    _filters: object,
    orderBy: string | void,
    orderByDesc: boolean | void,
    callbackFunc: GetPageCallbackFunc,
  ) => {
    // Get filtered devices first
    const filtered = devices.filter(device =>
      device.serial.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Apply sorting if specified
    let sortedDevices = filtered;
    if (orderBy) {
      sortedDevices = [...filtered].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        
        switch (orderBy) {
          case 'serial':
            aValue = a.serial;
            bValue = b.serial;
            break;
          case 'lastSeenAt':
            aValue = a.lastSeenAt || 0;
            bValue = b.lastSeenAt || 0;
            break;
          case 'status':
            // Sort by status priority: Online - Success > Online - Invalid > Offline > Inactive
            const getStatusPriority = (device: MqttDeviceListItem) => {
              if (!device.latestData) return 3; // Offline
              if (!device.latestData.active) return 4; // Inactive
              const now = Math.floor(Date.now() / 1000);
              const timeDiff = now - device.latestData.lastReadingAt;
              if (timeDiff < 86400) {
                return device.latestData.validData ? 1 : 2; // Online - Success : Online - Invalid
              }
              return 3; // Offline
            };
            aValue = getStatusPriority(a);
            bValue = getStatusPriority(b);
            break;
          case 'location':
            aValue = getDeviceLocation(a.latestData);
            bValue = getDeviceLocation(b.latestData);
            break;
          case 'latestData':
            // Sort by data preview
            aValue = a.latestData ? JSON.stringify(JSON.parse(a.latestData.jsonData)) : '';
            bValue = b.latestData ? JSON.stringify(JSON.parse(b.latestData.jsonData)) : '';
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return orderByDesc ? 1 : -1;
        if (aValue > bValue) return orderByDesc ? -1 : 1;
        return 0;
      });
    }
    
    // Apply pagination
    const paginatedDevices = sortedDevices.slice(offset, offset + limit);
    callbackFunc(sortedDevices.length, paginatedDevices);
  };

  const renderDeviceCard = (record: MqttDeviceListItem) => {
    const getLastSeen = () => {
      if (!record.lastSeenAt) return "Never";
      const date = new Date(record.lastSeenAt * 1000);
      return format(date, "yyyy-MM-dd HH:mm:ss");
    };

    const getStatus = () => {
      if (!record.latestData) return { text: "Offline", type: "danger" as const };
      
      // Check if device is inactive first
      if (!record.latestData.active) {
        return { text: "Inactive", type: "secondary" as const };
      }
      
      const now = Math.floor(Date.now() / 1000);
      const timeDiff = now - record.latestData.lastReadingAt;
      
      if (timeDiff < 86400) { // 24 hours
        // Check if most recent data is valid
        if (record.latestData.validData) {
          return { text: "Online - Success", type: "success" as const };
        } else {
          return { text: "Online - Invalid", type: "warning" as const };
        }
      } else {
        return { text: "Offline", type: "danger" as const };
      }
    };

    const getLatestDataPreview = () => {
      if (!record.latestData) return "No data available";
      
      try {
        const data = JSON.parse(record.latestData.jsonData);
        const keys = Object.keys(data).slice(0, 2); // Show first 2 keys
        return keys.map(key => `${key}: ${data[key]}`).join(", ");
      } catch (error) {
        console.error('Card rendering - Error parsing JSON data:', error, 'Raw data:', record.latestData.jsonData);
        return "Invalid data";
      }
    };

    const status = getStatus();

    return (
      <Card 
        key={record.serial} 
        size="small" 
        style={{ marginBottom: 8 }}
        bodyStyle={{ padding: 12 }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {/* Header: Serial Number */}
          <div>
            <Link to={`/mqtt-devices/${record.serial}`}>
              <Typography.Title level={5} style={{ margin: 0, fontSize: 16 }}>
                {record.serial}
              </Typography.Title>
            </Link>
          </div>

          {/* Status */}
          <div>
            <Typography.Text type={status.type} strong>
              Status: {status.text}
            </Typography.Text>
          </div>

          {/* Location */}
          <div>
            <Typography.Text strong style={{ fontSize: 12, color: '#666' }}>
              Location:
            </Typography.Text>
            <br />
            <Typography.Text style={{ fontSize: 14 }}>
              <EnvironmentOutlined style={{ marginRight: 4 }} />
              {getDeviceLocation(record.latestData || undefined)}
            </Typography.Text>
          </div>

          {/* Last Seen */}
          <div>
            <Typography.Text strong style={{ fontSize: 12, color: '#666' }}>
              Last seen:
            </Typography.Text>
            <br />
            <Typography.Text style={{ fontSize: 14 }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              {getLastSeen()}
            </Typography.Text>
          </div>

          {/* Latest Data Preview */}
          <div>
            <Typography.Text strong style={{ fontSize: 12, color: '#666' }}>
              Latest data:
            </Typography.Text>
            <br />
            <Typography.Text style={{ fontSize: 14 }}>
              {getLatestDataPreview()}
            </Typography.Text>
          </div>
        </Space>
      </Card>
    );
  };

  if (loading) {
    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <PageHeader
          title="Device Statistics Details"
          subTitle="Manage and monitor device statistics"
        />
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Typography.Text>Loading devices...</Typography.Text>
          </div>
        </div>
      </Space>
    );
  }

  if (displayedDevices.length === 0 && !loading) {
    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <PageHeader
          title="Device Statistics Details"
          subTitle="Manage and monitor device statistics"
        />
        
        {/* Search Bar */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search devices by serial number..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            style={{ width: '100%' }}
          />
        </Card>
        
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              {searchTerm 
                ? `No devices found matching "${searchTerm}". Try a different search term.`
                : "No devices found. Devices will appear here once they start sending data with valid serial numbers."
              }
            </span>
          }
        />
      </Space>
    );
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <PageHeader
        breadcrumbRender={() => (
          <Breadcrumb>
            <Breadcrumb.Item>
              <span>DevStat Details</span>
            </Breadcrumb.Item>
          </Breadcrumb>
        )}
        title="Device Statistics Details"
        subTitle="Manage and monitor device statistics"
        extra={[
          <Typography.Text key="device-count" type="secondary">
            <WifiOutlined style={{ marginRight: 4 }} />
            {searchTerm ? `${filteredDevices.length} of ${devices.length}` : filteredDevices.length} device{filteredDevices.length !== 1 ? 's' : ''}
          </Typography.Text>
        ]}
      />
      
      {/* Search Bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search devices by serial number..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
          style={{ width: '100%' }}
        />
      </Card>
      
      {isMobile ? (
        // Mobile view - show all devices as cards
        <div style={{ padding: '0 8px' }}>
          {displayedDevices.map((record) => renderDeviceCard(record))}
        </div>
      ) : (
        // Desktop view with table
        <ResponsiveDataTable 
          columns={columns} 
          getPage={getPage} 
          rowKey="serial"
          renderCard={renderDeviceCard}
        />
      )}
    </Space>
  );
}

export default MqttDevices;